// Auth Interceptor - Authentication middleware
//
// Wraps httpClient with authentication logic:
// - Proactive token refresh before expiry
// - Automatic retry on 401 errors
// - Skips auth logic for public endpoints

import httpClient from '../api/httpClient';
import { isTokenExpiringSoon, refreshAccessToken } from './tokenService';

const authInterceptor = async (endpoint, options = {}) => {
  // Skip interception for auth endpoints (login, register, password reset, etc)
  const isAuthEndpoint = endpoint.includes('/auth/');

  if (isAuthEndpoint) {
    return httpClient(endpoint, options);
  }

  // Proactively refresh token if it's about to expire (unless this is a retry)
  if (!options._retry) {
    const token = sessionStorage.getItem('accessToken');
    if (token && isTokenExpiringSoon(token)) {
      try {
        console.debug('[AuthInterceptor] Token expiring soon, proactively refreshing...');
        await refreshAccessToken();
      } catch (error) {
        console.warn('[AuthInterceptor] Proactive token refresh failed:', error.message);
        // Continue with request - will handle 401 if it happens
      }
    }
  }

  try {
    return await httpClient(endpoint, options);
  } catch (error) {
    // Handle 401 - Token expired, try to refresh and retry once
    if (error.status === 401 && !options._retry) {
      try {
        console.debug('[AuthInterceptor] Access token expired (401), refreshing...');
        await refreshAccessToken();
        console.debug('[AuthInterceptor] Token refreshed successfully, retrying request');

        return authInterceptor(endpoint, {
          ...options,
          _retry: true,
        });
      } catch (refreshError) {
        console.error('[AuthInterceptor] Token refresh failed:', refreshError.message);
        throw refreshError;
      }
    }

    throw error;
  }
};

export default authInterceptor;
